import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import MVMLogo from './MVMLogo';

const SplashScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Animate progress bar faster
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 5; // Faster progress
      });
    }, 20);

    // Start fade out after 1 second (reduced from 2s)
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, 1000);

    // Complete after fade animation (reduced from 2.5s to 1.3s)
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1300);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
    // onComplete is memoized and won't change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div 
      className={`fixed inset-0 z-[100000] flex flex-col items-center justify-center transition-opacity duration-500 ${
        fadeOut ? 'opacity-0' : 'opacity-100'
      }`}
      style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #0891b2 50%, #0284c7 100%)'
      }}
    >
      {/* Animated background circles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo Icon */}
        <div className="relative mb-6">
          <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center shadow-2xl border border-white/30 animate-bounce" style={{ animationDuration: '2s' }}>
            <MVMLogo size="xl" variant="light" />
          </div>
          {/* Sparkle decorations */}
          <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-yellow-300 animate-pulse" />
          <Sparkles className="absolute -bottom-1 -left-1 w-4 h-4 text-yellow-300 animate-pulse" style={{ animationDelay: '0.3s' }} />
        </div>

        {/* App Name */}
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2 tracking-tight">
          MyVenueMate
        </h1>
        
        {/* Tagline */}
        <p className="text-white/80 text-base sm:text-lg mb-8 font-medium">
          Venue Management Made Easy
        </p>

        {/* Progress Bar */}
        <div className="w-48 sm:w-64 h-1.5 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
          <div 
            className="h-full bg-gradient-to-r from-white to-yellow-200 rounded-full transition-all duration-100 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Loading Text */}
        <p className="mt-4 text-white/60 text-sm animate-pulse">
          Loading...
        </p>
      </div>

      {/* Bottom branding */}
      <div className="absolute bottom-8 text-center">
        <p className="text-white/50 text-xs">
          Powered by
        </p>
        <p className="text-white/70 text-sm font-semibold">
          MYONK
        </p>
      </div>
    </div>
  );
};

export default SplashScreen;
